import * as api from './api';
import { Item } from './types';

/**
 * Deep-duplicate an item and its whole subtree. The root copy is placed under the original's
 * parent at `rootPosition` (the caller decides where — typically just below the original) and
 * gets a "(copy)" suffix; descendants keep their titles and relative order. Nodes are created
 * parent-first (breadth-first) so each child's new parent already exists when it is created —
 * the API rejects a parent_id that isn't present yet.
 *
 * Returns the created items (root first) for an optimistic append; callers should still
 * reload afterwards to pick up server-assigned keys/ordering.
 */
export async function duplicateItem(items: Item[], rootId: string, rootPosition: number): Promise<Item[]> {
  const root = items.find((i) => i.id === rootId);
  if (!root) return [];

  const childrenBy = new Map<string, Item[]>();
  for (const it of items) {
    if (it.parent_id) {
      const arr = childrenBy.get(it.parent_id) ?? [];
      arr.push(it);
      childrenBy.set(it.parent_id, arr);
    }
  }

  type Task = { src: Item; parentId: string | null; position: number; title: string };
  const queue: Task[] = [
    { src: root, parentId: root.parent_id, position: rootPosition, title: `${root.title} (copy)` },
  ];
  const created: Item[] = [];

  while (queue.length) {
    const t = queue.shift()!;
    const copy = await api.createItem({
      board_id: t.src.board_id,
      parent_id: t.parentId,
      title: t.title,
      description: t.src.description,
      status: t.src.status,
      priority: t.src.priority,
      assignee_id: t.src.assignee_id,
      pdm_id: t.src.pdm_id,
      source: t.src.source,
      labels: t.src.labels,
      start_date: t.src.start_date,
      end_date: t.src.end_date,
      position: t.position,
    });
    created.push(copy);
    const kids = [...(childrenBy.get(t.src.id) ?? [])].sort((a, b) => a.position - b.position);
    for (const k of kids) queue.push({ src: k, parentId: copy.id, position: k.position, title: k.title });
  }

  return created;
}
